import { enableMapSet } from "immer";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { DataKeys } from "../types";

type Context = string;
type PlaintextDataKey = string;
type EncryptedDataKey = string;

interface DataKeysState extends DataKeysCache {
    readonly dataKeys: Map<Context, Map<EncryptedDataKey, PlaintextDataKey>>;
}

export interface DataKeysCache {
    findDataKeyByContext: (context: Context) => DataKeys | undefined;
    findDataKeyByEncryptionKey: (context: Context, encryptedDataKey: EncryptedDataKey) => PlaintextDataKey | undefined;
    addDataKey: (context: Context, keys: DataKeys) => void;
    removeDataKey: (context: Context) => void;
}

enableMapSet();
export const useDataKeysStore = create<DataKeysState>()(
    immer(
        devtools(
            (set, get) => ({
                dataKeys: new Map<Context, Map<EncryptedDataKey, PlaintextDataKey>>(),
                findDataKeyByContext: (context: Context): DataKeys | undefined => {
                    const [encryptedDataKey, plaintextDataKey] =
                        get().dataKeys.get(context)?.entries().next().value || [];
                    return encryptedDataKey && plaintextDataKey
                        ? {
                              plaintextDataKey,
                              encryptedDataKey,
                          }
                        : undefined;
                },
                findDataKeyByEncryptionKey: (
                    context: Context,
                    encryptedDataKey: EncryptedDataKey,
                ): PlaintextDataKey | undefined => {
                    const existingDataKey = get().dataKeys.get(context);
                    return existingDataKey?.get(encryptedDataKey);
                },
                addDataKey: (context: Context, { encryptedDataKey, plaintextDataKey }: DataKeys): void =>
                    set(
                        (state) => {
                            const existingDataKey = state.dataKeys.get(context);
                            if (existingDataKey) {
                                existingDataKey.set(encryptedDataKey, plaintextDataKey);
                            } else {
                                state.dataKeys.set(context, new Map([[encryptedDataKey, plaintextDataKey]]));
                            }
                        },
                        false,
                        "addDataKey",
                    ),
                removeDataKey: (context: Context): void =>
                    set((state) => state.dataKeys.delete(context), false, "removeDataKey"),
            }),
            {
                enabled: import.meta.env.VITE_ENV_TYPE !== "production",
                store: "data-keys",
                name: "workspace/data-keys",
                serialize: {
                    options: {
                        map: true,
                    },
                },
            },
        ),
    ),
);
